import { useState } from 'react';
import { PlusIcon, XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { inviteFolderMembers } from '../../services/UserPacketCollectionService';
import { Mixpanel } from '../../services/Mixpanel';
import { isValidEmail } from '../../utils/email';

const MAX_INVITES = 25;

/**
 * Invite colleagues to a folder by email. Each new invitee gets the team-invite email (the
 * "Join The Team" CTA links to the folder's /c/:id page) and becomes a pending member, reconciled
 * to their account the first time they open the folder while signed in.
 *
 * Shared by the folder page's invite modal and the My Resources share modal so both surfaces
 * invite members identically. `onInvited` fires after invites send (e.g. to auto-close a modal).
 */
export const FolderInviteForm = ({ collectionId, folderName, onInvited }) => {
  const [emails, setEmails] = useState(['']);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const updateEmail = (index, value) =>
    setEmails((prev) => prev.map((e, i) => (i === index ? value : e)));
  const addEmailField = () =>
    setEmails((prev) => (prev.length >= MAX_INVITES ? prev : [...prev, '']));
  const removeEmailField = (index) => setEmails((prev) => prev.filter((_, i) => i !== index));

  const onSubmit = async () => {
    const inviteEmails = emails.map((e) => e.trim()).filter(Boolean);
    if (inviteEmails.length === 0) {
      setError('Add at least one email address.');
      return;
    }
    const invalid = inviteEmails.filter((e) => !isValidEmail(e));
    if (invalid.length > 0) {
      setError(`These email addresses look invalid: ${invalid.join(', ')}`);
      return;
    }

    setError(null);
    setIsSubmitting(true);
    try {
      await inviteFolderMembers(collectionId, inviteEmails);
      Mixpanel.track('Team Members Invited', {
        collection_id: collectionId,
        invite_count: inviteEmails.length,
      });
      setEmails(['']);
      setSuccess(true);
      onInvited?.();
    } catch {
      setError('Something went wrong sending invites. Please try again.');
      Mixpanel.track('Team Members Invite Failed', { collection_id: collectionId });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className='flex flex-col items-center gap-y-2 py-6 text-center'>
        <CheckCircleIcon className='w-10 h-10 text-emerald-600' />
        <p className='text-gray-800 font-medium'>Invitations sent!</p>
        <p className='text-sm text-gray-500'>
          They&apos;ll get an email inviting them to join
          {folderName ? ` ${folderName}` : ' this folder'}.
        </p>
        <button
          type='button'
          onClick={() => setSuccess(false)}
          className='mt-1 text-sm font-medium text-emerald-600 hover:text-emerald-700'>
          Invite more
        </button>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-y-4'>
      <p className='text-sm text-gray-600'>
        Invite colleagues by email. They&apos;ll be able to view this folder and add their own
        resources.
      </p>
      <div className='flex flex-col gap-y-2'>
        {emails.map((email, index) => (
          <div key={index} className='flex items-center gap-x-2'>
            <input
              type='email'
              autoFocus={index === 0}
              value={email}
              onChange={(e) => updateEmail(index, e.target.value)}
              className='flex-1 px-3 py-2 text-md text-gray-900 placeholder-gray-500 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500'
              placeholder='colleague@school.org'
            />
            {emails.length > 1 && (
              <button
                type='button'
                aria-label='Remove email'
                onClick={() => removeEmailField(index)}
                className='p-1.5 text-gray-400 hover:text-gray-600'>
                <XMarkIcon className='w-5 h-5' />
              </button>
            )}
          </div>
        ))}
        {emails.length < MAX_INVITES && (
          <button
            type='button'
            onClick={addEmailField}
            className='inline-flex items-center gap-x-1 text-sm font-medium text-emerald-600 hover:text-emerald-700 self-start'>
            <PlusIcon className='w-4 h-4' />
            Add another
          </button>
        )}
      </div>

      {error && <p className='text-sm text-red-600'>{error}</p>}

      <button
        onClick={onSubmit}
        disabled={isSubmitting}
        className='bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-3 rounded-md font-semibold transition-colors disabled:bg-emerald-300'>
        {isSubmitting ? 'Sending…' : 'Send invites'}
      </button>
    </div>
  );
};
